import { motion } from "framer-motion";
import { Bot, Sparkles, ArrowRight } from "lucide-react";
import { openAbayAI, askAbayAI } from "@/lib/abayAiBus";
import { useTranslation } from "react-i18next";

const MOBILE_SUGGESTION_KEYS = ["ai.s1", "ai.s2", "ai.s3"];

export const AbayAICard = () => {
  const { t } = useTranslation();
  const EXAMPLE_QUERY = t("ai.exampleQuery");
  return (
    <section className="abay-container py-8 sm:py-12" data-testid="abay-ai-card-section">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.45 }}
        data-testid="home-abay-ai-card"
        className="relative overflow-hidden rounded-[20px] border border-white/10 p-6 shadow-[0_20px_50px_rgba(8,15,40,0.28)] sm:rounded-3xl sm:p-8"
        style={{ background: "linear-gradient(120deg, #0B1A3A 0%, #12245A 60%, #0E2E52 100%)" }}
      >
        {/* Decorative sparkles */}
        <Sparkles className="pointer-events-none absolute right-40 top-8 hidden h-4 w-4 text-[#34D399]/70 sm:block" />
        <Sparkles className="pointer-events-none absolute right-24 top-20 hidden h-3 w-3 text-white/40 sm:block" />
        <Sparkles className="pointer-events-none absolute right-56 bottom-10 hidden h-3 w-3 text-[#7AA0FF]/60 lg:block" />

        {/* ===================== MOBILE variant ===================== */}
        <div className="relative lg:hidden">
          <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#10B981]/20 blur-3xl" />

          <div className="relative flex items-center justify-between gap-4">
            <span className="flex items-center gap-2 text-xl font-extrabold tracking-tight text-white">
              <Sparkles className="h-5 w-5 text-[#34D399]" /> {t("ai.title")}
            </span>
            {/* Robot mascot — ~15% larger, soft glow, never cropped */}
            <div className="relative flex h-[4.9rem] w-[4.9rem] shrink-0 items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-[#10B981]/25 blur-xl" />
              <span className="relative flex h-[4.2rem] w-[4.2rem] items-center justify-center rounded-2xl bg-white text-[#0B1A3A] shadow-[0_12px_30px_rgba(0,0,0,0.35)]">
                <Bot className="h-10 w-10" strokeWidth={1.8} />
                <span className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-[#34D399] shadow-[0_0_12px_3px_rgba(52,211,153,0.7)]" />
              </span>
            </div>
          </div>

          <p className="relative mt-2.5 text-sm leading-relaxed text-white/75">
            {t("ai.subtitleLong")}
          </p>

          {/* 3 suggestion cards */}
          <div className="relative mt-5 flex flex-col gap-2.5">
            {MOBILE_SUGGESTION_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                data-testid="abay-ai-mobile-suggestion"
                onClick={() => askAbayAI(t(key))}
                className="group flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3.5 text-left text-sm font-medium text-white/90 transition-colors duration-200 hover:border-white/20 hover:bg-white/10"
              >
                {t(key)}
                <ArrowRight className="h-4 w-4 shrink-0 text-white/50 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-white" />
              </button>
            ))}
          </div>

          {/* Full-width gradient CTA */}
          <button
            type="button"
            data-testid="home-abay-ai-ask-button-mobile"
            onClick={() => openAbayAI()}
            className="abay-ask-gradient tap-press relative mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-2xl text-[15px] font-semibold text-white shadow-[0_12px_28px_rgba(16,185,129,0.28)] transition-[filter] duration-200 hover:brightness-105"
          >
            <Sparkles className="h-5 w-5" /> {t("ai.cta")}
          </button>
        </div>

        {/* ===================== DESKTOP variant (approved — unchanged) ===================== */}
        <div className="relative hidden grid-cols-[1fr_auto] items-center gap-6 lg:grid">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-2 text-xl font-extrabold tracking-tight text-white sm:text-2xl">
              <Sparkles className="h-5 w-5 text-[#34D399]" /> {t("ai.title")}
            </span>
            <p className="mt-2 text-sm leading-relaxed text-white/75 sm:text-base">
              {t("ai.subtitleShort")}
            </p>

            {/* Example query bubble */}
            <div className="mt-4 inline-block max-w-md rounded-2xl rounded-bl-sm bg-white/10 px-4 py-3 text-sm font-medium text-white/90 ring-1 ring-white/15 backdrop-blur-sm">
              “{EXAMPLE_QUERY}”
            </div>

            <div className="mt-5">
              <button
                type="button"
                data-testid="home-abay-ai-ask-button"
                onClick={() => openAbayAI(EXAMPLE_QUERY)}
                className="tap-press inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(30,58,138,0.4)] transition-[filter,box-shadow] duration-200 hover:brightness-[1.06]"
                style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #10B981 130%)" }}
              >
                {t("ai.cta")}
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#10B981]">
                  <ArrowRight className="h-3.5 w-3.5 text-white" />
                </span>
              </button>
            </div>
          </div>

          {/* Robot / AI avatar */}
          <div className="flex justify-self-center">
            <div className="relative flex h-36 w-36 items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-[#1E3A8A]/40 blur-2xl" />
              <div className="absolute inset-4 rounded-full bg-gradient-to-br from-white/15 to-white/5 ring-1 ring-white/20 backdrop-blur-md" />
              <motion.span
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="relative flex h-20 w-20 items-center justify-center rounded-3xl bg-white text-[#0B1A3A] shadow-[0_12px_30px_rgba(0,0,0,0.35)]"
              >
                <Bot className="h-11 w-11" strokeWidth={1.8} />
              </motion.span>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
